import * as z from "zod";

import { createUserOrgSettingsHooks } from "@/hooks/userOrgSettingsHooks";
import type { NamedOrg } from "@/lib/named-org";

export function proofMembersKey(org: unknown) {
  return ["proof-members", org] as const;
}

export const PROOF_TRIGGERS = [
  "push",
  "opened",
  "agent_decided",
  "manual",
] as const;
export type ProofTrigger = (typeof PROOF_TRIGGERS)[number];

/** The three modes the Proof settings offer. */
export const PROOF_MODES = ["always", "agent_decided", "manual"] as const;
export type ProofMode = (typeof PROOF_MODES)[number];

/** `push` and `opened` are migrating to `agent_decided`, so they read as it, e.g. `"opened"` → `"agent_decided"`. */
export const ProofModeField = z
  .enum([...PROOF_MODES, "push", "opened"])
  .transform((mode): ProofMode =>
    mode === "push" || mode === "opened" ? "agent_decided" : mode,
  );

const UserProofSettings = z
  .object({
    proof_trigger: z.enum(PROOF_TRIGGERS),
    /** Kept in step with proof_trigger for backend pods that still read it. */
    proof_enabled: z.boolean().optional(),
    /** The member's own mode; null follows the team's. */
    proof_mode: ProofModeField.nullable().optional().default(null),
    proof_team_mode: ProofModeField.optional().default("manual"),
    /** An admin has finished the team Proof setup. */
    proof_team_set_up: z.boolean().optional().default(false),
  })
  // Old backend pods may still resolve Proof off through the boolean.
  .transform((settings) =>
    settings.proof_enabled === false
      ? { ...settings, proof_trigger: "manual" as const }
      : settings,
  );
export type UserProofSettings = z.infer<typeof UserProofSettings>;

const hooks = createUserOrgSettingsHooks<UserProofSettings>({
  resource: "user-proof-settings",
  errorMessage: "Failed to update Proof settings",
  parse: (data) => UserProofSettings.parse(data),
  requestBody: (update) => ({ proof_trigger: update.proof_mode }),
  onSaved: (queryClient, org) =>
    void queryClient.invalidateQueries(proofMembersKey(org)),
});

export const useUserProofSettings = hooks.useSettings;

/** The mode the member's PRs run on, e.g. own `null` with team `"always"` → `"always"`. */
export function effectiveProofMode(settings: UserProofSettings): ProofMode {
  return settings.proof_mode ?? settings.proof_team_mode;
}

/** Saves the member's own mode; `null` follows the team again. */
export function useUpdateUserProofMode(org: NamedOrg | null | undefined) {
  const { mutate, mutateAsync, ...mutation } = hooks.useUpdateSettings(org);
  return {
    ...mutation,
    mutate: (mode: ProofMode | null, options?: Parameters<typeof mutate>[1]) =>
      mutate({ proof_mode: mode }, options),
    mutateAsync: (mode: ProofMode | null) => mutateAsync({ proof_mode: mode }),
  };
}
